import { isProdLikeEnvironment } from '@commenda/utils/constants';
import mixpanel from 'mixpanel-browser';

export * from './events';
export * from './sources';
export * from './surfaceAreas';

export const initMixpanel = ({
  trackPageview = true,
}: Readonly<{ trackPageview?: boolean }> = {}): boolean => {
  const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN;
  if (!token) {
    console.warn(
      'Mixpanel token is missing! Check your environment variables.'
    );
    return false;
  }

  const isProduction = isProdLikeEnvironment(
    process.env.NEXT_PUBLIC_CLIENT_ENV || ''
  );

  mixpanel.init(token, {
    debug: !isProduction,
    track_pageview: trackPageview,
    persistence: 'localStorage',
    ignore_dnt: true,
    api_host: '/analytics',
    // Enable session replay only in production
    ...(isProduction && {
      record_sessions_percent: 100,
      // Unmask non-input text elements (labels, headings, etc.)
      // Input fields will still be masked for privacy
      record_mask_text_selector: '',
    }),
  });
  return true;
};

/**
 * Separate Mixpanel sub-instance for the AI-products project ("Production", 4051185).
 * Only deliberately-chosen events go here (active_user; backend sends ENTITY_WRITE and chat
 * events server-side) — no pageview autotrack, no session replay, to keep that project clean.
 * No-op when NEXT_PUBLIC_MIXPANEL_AI_TOKEN is unset.
 */
let aiMixpanel: typeof mixpanel | null = null;

export const initAiMixpanel = () => {
  const token = process.env.NEXT_PUBLIC_MIXPANEL_AI_TOKEN;
  if (!token || aiMixpanel) return;

  const isProduction = isProdLikeEnvironment(
    process.env.NEXT_PUBLIC_CLIENT_ENV || ''
  );

  aiMixpanel = mixpanel.init(
    token,
    {
      debug: !isProduction,
      track_pageview: false,
      persistence: 'localStorage',
      ignore_dnt: true,
      api_host: '/analytics',
    },
    'ai_project'
  );
};

export const identifyAiUser = (distinctId: string) => {
  try {
    aiMixpanel?.identify(distinctId);
  } catch (error) {
    if (process.env.NODE_ENV === 'development')
      console.warn('Mixpanel (AI) identify failed:', error);
  }
};

export const trackAiEvent = (
  eventName: string,
  properties?: Record<string, unknown>
) => {
  try {
    aiMixpanel?.track(eventName, properties);
  } catch (error) {
    if (process.env.NODE_ENV === 'development')
      console.warn('Mixpanel (AI) track failed:', error);
  }
};

/**
 * Registers the tracking plan's global properties (company_id, is_internal, app) as super props on
 * the AI-products ("Production") instance only — the legacy project's event shape must not change.
 * Call with whatever is known at the time; later calls merge additional props.
 */
export const registerAiCoreProps = (props: {
  email?: string | null;
  companyId?: number | string | null;
  app?: string;
}) => {
  try {
    const superProps: Record<string, unknown> = {};
    if (props.app) superProps.app = props.app;
    if (typeof props.email === 'string' && props.email.length > 0) {
      superProps.is_internal = props.email
        .toLowerCase()
        .endsWith('@commenda.io');
    }
    const companyId = Number(props.companyId);
    if (
      props.companyId !== null &&
      props.companyId !== undefined &&
      Number.isInteger(companyId) &&
      companyId > 0
    ) {
      superProps.company_id = companyId;
    }
    if (Object.keys(superProps).length > 0) aiMixpanel?.register(superProps);
  } catch (error) {
    if (process.env.NODE_ENV === 'development')
      console.warn('Mixpanel (AI) register failed:', error);
  }
};

function safeWrap<T extends (...args: any[]) => any>(
  method: T,
  methodName: string
): T {
  return function (...args: Parameters<T>): ReturnType<T> {
    try {
      const result = method.apply(mixpanel, args);
      if (result instanceof Promise) {
        return result.catch((error) => {
          if (process.env.NODE_ENV === 'development')
            console.warn(`Mixpanel ${methodName} failed:`, error);
        }) as ReturnType<T>;
      }
      return result as ReturnType<T>;
    } catch (error) {
      if (process.env.NODE_ENV === 'development')
        console.warn(`Mixpanel ${methodName} failed:`, error);
      return undefined as ReturnType<T>;
    }
  } as T;
}

mixpanel.track = safeWrap(
  mixpanel.track.bind(mixpanel) as (...args: any[]) => any,
  'track'
) as typeof mixpanel.track;

export default mixpanel;
